import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

import { useAppDispatch, useAppSelector, useCurrentUser } from 'hooks';
import LayoutSignUp from 'components/layouts/LayoutSignUp';
import { getUrlParams } from 'utils/url';
import path from 'common/path';
import {
  deleteBusinessesId,
  hasOtpSession,
  getUserEmail,
  setUserEmail,
  setProfileId,
} from 'utils/authService';

import ProfileContainer from './components/ProfileContainer';
import { stashEnrollPrefill, takeEnrollPrefill } from './prefill';
import { getOnboarding, getCountries } from './selectors';
import { fetchOrCreatePerson, fetchCountries } from './thunks';
import {
  resetState,
  setIsLoading,
  setRedirectToDashboard,
  applyEnrollPrefill,
} from './profileSlice';

const Profile = () => {
  const dispatch = useAppDispatch();
  const location: any = useLocation();
  const { user, isAuthenticated } = useCurrentUser();

  const onboarding = useAppSelector(getOnboarding);
  const countries = useAppSelector(getCountries);
  const params: any = getUrlParams();
  const otpSession = hasOtpSession();

  const { businessId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (businessId) {
      setProfileId(+businessId);

      if (params?.$email) {
        setUserEmail(params.$email);
      }

      navigate(path.sso);
      return;
    }

    // the visitor came from the sign-up form: the profile loaded after the code check, so fill the prefill now
    const restorePrefill = (email: string) => {
      const prefill = takeEnrollPrefill(email);

      if (prefill) {
        dispatch(applyEnrollPrefill(prefill));
      }
    };

    if (!onboarding && (isAuthenticated || otpSession)) {
      let email = user?.username || getUserEmail();

      if (!email && otpSession && params?.$email) {
        email = params.$email;
        dispatch(setRedirectToDashboard(false));
      }

      if (email) {
        const loaded = dispatch(
          fetchOrCreatePerson({
            username: email,
            name: user?.name || '',
            checkStep: location?.state?.checkStep ?? true,
          }),
        );

        if (otpSession) {
          Promise.resolve(loaded).then(() => restorePrefill(email));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, onboarding, user, location, isAuthenticated]);

  useEffect(() => {
    const resetProfile = async () => {
      deleteBusinessesId();
      await dispatch(resetState(user));
    };

    if (onboarding) {
      resetProfile();
    }
  }, [dispatch, onboarding, user]);

  useEffect(() => {
    if (!countries.length) {
      dispatch(fetchCountries());
    }
  }, [countries.length, dispatch]);

  useEffect(() => {
    // not signed in: the email code comes first. Send the visitor to the sign-up form with their email (no
    // message: nothing says the email is taken), and keep the prefill for when the code has been checked
    const sendToEnroll = () => {
      if (params?.$email == null) {
        dispatch(setIsLoading(false));
        return;
      }

      stashEnrollPrefill(params);
      navigate(`${path.enroll}?email=${encodeURIComponent(params.$email)}`, {
        replace: true,
      });
    };

    if (!isAuthenticated && !otpSession) {
      dispatch(setRedirectToDashboard(false));
      sendToEnroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <LayoutSignUp>
      <ProfileContainer />
    </LayoutSignUp>
  );
};

export default Profile;
